import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuthStore } from '../store/auth';
import { useUIStore } from '../store/ui';
import Loader from '../components/Loader';

interface Listing {
  id: string;
  userId: number;
  title: string;
  description: string;
  category: string;
  price?: number;
  currency: string;
  images: string[];
  countryCode: string;
  regionId: number;
  views: number;
  favorites: number;
  createdAt: string;
  user: {
    firstName: string;
    lastName?: string;
    rating: number;
    telegramId: number;
  };
}

export default function ListingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { showToast } = useUIStore();
  const [listing, setListing] = useState<Listing | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    loadListing();
  }, [id]);

  const loadListing = async () => {
    if (!id) return;
    try {
      const response = await apiService.getListing(id);
      setListing(response.data.data);
    } catch (error) {
      console.error('Error loading listing:', error);
      showToast('Объявление не найдено', 'error');
      navigate('/listings');
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    const symbols: Record<string, string> = {
      RUB: '₽',
      KZT: '₸',
      BYN: 'Br'
    };
    return `${amount.toLocaleString('ru-RU')} ${symbols[currency] || currency}`;
  };

  const handleContact = async () => {
    if (!listing || !user) return;
    
    // Haptic feedback
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
    
    try {
      const response = await apiService.createChat({
        participantTelegramId: listing.user.telegramId,
        referenceType: 'listing',
        referenceId: listing.id
      });
      navigate(`/chats/${response.data.data.id}`);
    } catch (error) {
      console.error('Error creating chat:', error);
      showToast('Ошибка при создании чата', 'error');
    }
  };

  if (isLoading) {
    return <Loader className="py-12" />;
  }

  if (!listing) {
    return null;
  }

  const isOwner = user?.telegramId === listing.user.telegramId;

  return (
    <div className="space-y-4 -mx-4">
      {/* Images */}
      <div className="relative aspect-square bg-tg-theme-secondary-bg">
        {listing.images.length > 0 ? (
          <>
            <img
              src={listing.images[currentImageIndex]}
              alt={listing.title}
              className="w-full h-full object-cover"
            />
            {listing.images.length > 1 && (
              <>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1">
                  {listing.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full ${
                        index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
                <button
                  onClick={() => setCurrentImageIndex((prev) => 
                    prev > 0 ? prev - 1 : listing.images.length - 1
                  )}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white w-10 h-10 rounded-full"
                >
                  ←
                </button>
                <button
                  onClick={() => setCurrentImageIndex((prev) => 
                    prev < listing.images.length - 1 ? prev + 1 : 0
                  )}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white w-10 h-10 rounded-full"
                >
                  →
                </button>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-6xl text-tg-theme-hint">
            📷
          </div>
        )}
      </div>

      <div className="px-4 space-y-4">
        {/* Price */}
        {listing.price !== undefined && listing.price !== null ? (
          <p className="text-2xl font-bold text-tg-theme-button">
            {formatPrice(listing.price, listing.currency)}
          </p>
        ) : (
          <p className="text-2xl font-bold text-tg-theme-link">Договорная</p>
        )}

        {/* Title */}
        <h1 className="text-xl font-bold">{listing.title}</h1>

        {/* Stats */}
        <div className="flex items-center gap-4 text-sm text-tg-theme-hint">
          <span>👁 {listing.views} просмотров</span>
          <span>❤️ {listing.favorites} в избранном</span>
        </div>

        {/* Description */}
        <div className="card">
          <h3 className="font-semibold mb-2">Описание</h3>
          <p className="text-sm whitespace-pre-wrap">{listing.description}</p>
        </div>

        {/* Seller */}
        <div 
          className="card cursor-pointer"
          onClick={() => navigate(`/profile/${listing.user.telegramId}`)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-tg-theme-button flex items-center justify-center text-white text-xl">
                {listing.user.firstName[0]}
              </div>
              <div>
                <p className="font-semibold">
                  {listing.user.firstName} {listing.user.lastName}
                </p>
                <p className="text-sm text-tg-theme-hint">
                  ⭐️ {listing.user.rating.toFixed(1)} рейтинг
                </p>
              </div>
            </div>
            <span className="text-tg-theme-hint">→</span>
          </div>
        </div>

        {/* Actions */}
        {isOwner ? (
          <div className="space-y-2">
            <button
              onClick={() => navigate(`/listings/${id}/edit`)}
              className="btn-secondary w-full"
            >
              ✏️ Редактировать
            </button>
          </div>
        ) : (
          <button
            onClick={handleContact}
            className="btn-primary w-full"
          >
            💬 Написать продавцу
          </button>
        )}

        {/* Date */}
        <p className="text-sm text-tg-theme-hint text-center">
          Опубликовано {new Date(listing.createdAt).toLocaleDateString('ru-RU')}
        </p>
      </div>
    </div>
  );
}
